import React, { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { LabelFilter } from "./TranslationStats";
import Icon from "../../ui/Icon";
import { ALLOWED_REVIEW_TRANSLATION_STATUS, getButtonStyles } from "../../../utils/ApiUtils/TranslationHelper";
import KeyboardShortcuts from "../../KeyBoardShortCuts";
import { TranslationStatus } from "../../../model/Translation";
import TranslationProgressBar from "../progress/TranslationLabelsProgressBar";

interface TranslationStatsHeaderProps {
  totalItems: number;
  pendingItems: number;
  translatedItems: number;
  initialLabelsToTranslate: number;
  needsReviewItems?: number;
  activeFilter: LabelFilter;
  onFilterChange: (filter: LabelFilter) => void;
  onSendForReview?: () => void;
  onClose?: () => void;
  onToggleExpand: () => void;
  isExpanded: boolean;
  review: boolean;
  onToggleShortCuts: () => void;
}

const TranslationStatsHeader: React.FC<TranslationStatsHeaderProps> = ({
  totalItems,
  pendingItems,
  translatedItems,
  initialLabelsToTranslate,
  needsReviewItems,
  activeFilter,
  onFilterChange,
  onSendForReview,
  onClose,
  onToggleExpand,
  isExpanded,
  review,
  onToggleShortCuts,
}) => {
  const handleFilterClick = (filter: LabelFilter) => {
    onFilterChange(filter);
  };

  return (
    <div
      className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors duration-200 min-h-[80px] relative z-10"
      onClick={onToggleExpand}
    >
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-800 whitespace-nowrap">Translation Progress</h3>
        <div className={`transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}>
          <Icon icon="down" />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          className={getButtonStyles("total", activeFilter, "teal")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("total");
          }}
        >
          {totalItems} Total
        </button>
        <button
          className={getButtonStyles("pending", activeFilter, "yellow")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("pending");
          }}
        >
          <Icon icon="modify" />
          {pendingItems} Pending
        </button>
        <button
          className={getButtonStyles("translated", activeFilter, "purple")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("translated");
          }}
        >
          <Icon icon="translate" />
          {translatedItems} Translated
        </button>
      </div>

      <TranslationProgressBar initial={initialLabelsToTranslate} pending={pendingItems} />

      <div className="flex items-center gap-2">
        {/* Small Rounded Info Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleShortCuts();
          }}
          className="w-8 h-8 rounded-full text-primary-dark shadow-md hover:shadow-lg flex items-center justify-center text-xs font-semibold border border-primary-light"
          title="View keyboard shortcuts"
        >
          <Icon icon="info" />
        </button>
        <Tooltip title={"Close"}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            startIcon={<Icon icon="close" />}
            color="error"
            className="min-w-fit"
          >
            Close
          </Button>
        </Tooltip>
        <Tooltip title={!review ? "send to review" : " cannot send to review"}>
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSendForReview?.();
              }}
              startIcon={<Icon icon="step" />}
              color="primary"
              className="min-w-fit flex"
              disabled={review}
            >
              <span>REVIEW</span>
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default TranslationStatsHeader;
